import React, { useState, useEffect, useCallback } from 'react';
import {
    Modal, Box, Typography, Button, Grid, TextField, CircularProgress, MenuItem
} from '@mui/material';
import { toast } from 'react-toastify';
import {
    getAllMakerPlanDetails, getAllMakerPlans
} from '../../../services/adminServices/MakerPlanServices';
import { getAllTopicsOfModule } from '../../../services/adminServices/ModuleServices';
import { addPracticeNotes, UpdatePracticeNotes } from '../../../services/mentorServices/PracticeNotesServices';

const INITIAL_FORM_STATE = {
    makerPlan: '',
    module: '',
    day: '',
    topic: '',
};

const NotesPracticeModal = ({ open, onClose, selection, selectedRow, fetchData, setSelectedRow }) => {
    const [formValues, setFormValues] = useState(INITIAL_FORM_STATE);
    const [formErrors, setFormErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [makerPlanOptions, setMakerPlanOptions] = useState([]);
    const [makerModuleOptions, setMakerModuleOptions] = useState([]);
    const [topicOptions, setTopicOptions] = useState([]);

    useEffect(() => {
        if (!open) {
            resetFormState();
            setSelectedRow(null);
        }
    }, [open]);

    useEffect(() => {
        if (selectedRow) {
            fetchModules(selectedRow?.makerplanID);
            fetchTopics(selectedRow?.moduleID);
            setFormValues({
                makerPlan: selectedRow?.makerplanID || '',
                module: selectedRow?.moduleID || '',
                topic: selectedRow?.topicID || '',
                day: selectedRow.note_name,
            });
        } else {
            resetFormState();
        }
        setFormErrors({});
    }, [selectedRow]);

    useEffect(() => {
        const fetchMakerPlans = async () => {
            try {
                const response = await getAllMakerPlans();
                setMakerPlanOptions(response?.data.data || []);
            } catch (error) {
                toast.error(error?.response?.data.message || error?.message || error?.error);
            }
        };
        fetchMakerPlans();
    }, []);

    const fetchModules = useCallback(async (makerPlanId) => {
        try {
            const response = await getAllMakerPlanDetails(makerPlanId);
            setMakerModuleOptions(response?.data.data || []);
        } catch (error) {
            toast.error(error?.response?.data.message || error?.message || error?.error);
        }
    }, []);

    const fetchTopics = useCallback(async (moduleID) => {
        try {
            const response = await getAllTopicsOfModule(moduleID);
            setTopicOptions(response?.data.data || []);
        } catch (error) {
            toast.error(error?.response?.data.message || error?.message || error?.error);
        }
    }, []);

    const resetFormState = () => {
        setFormValues(INITIAL_FORM_STATE);
        setFormErrors({});
        setIsLoading(false);
    };

    const handleInputChange = ({ target: { name, value } }) => {
        if (['makerPlan', 'module', 'topic', 'day'].includes(name)) {
            setFormErrors((prev) => ({ ...prev, [name]: '' }));

            if (name === 'makerPlan') {
                fetchModules(value);
                setFormValues((prev) => ({
                    ...prev,
                    makerPlan: value,
                    module: '',
                    topic: '',
                }));
                return;
            }
            if (name === 'module') {
                fetchTopics(value);
                setFormValues((prev) => ({
                    ...prev,
                    module: value,
                    topic: '',
                }));
                return;
            }
            setFormValues((prev) => ({ ...prev, [name]: value }));
            return;
        }

        if (name === 'day' && !/^[0-9]*$/.test(value)) {
            setFormErrors((prev) => ({ ...prev, [name]: 'Please enter valid numbers only' }));
        } else {
            setFormErrors((prev) => ({ ...prev, [name]: '' }));
            setFormValues((prev) => ({ ...prev, [name]: value }));
        }
    };

    const validateFields = () => {
        const errors = {};
        const { makerPlan, module, day, topic } = formValues;
        if (!makerPlan) errors.makerPlan = 'Maker Plan is required*';
        if (!module) errors.module = 'Module is required*';
        if (!day) errors.day = 'Day is required*';
        if (!topic) errors.topic = 'Topic Name is required*';

        return errors;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const errors = validateFields();
        if (Object.keys(errors).length) return setFormErrors(errors);

        setIsLoading(true);
        try {
            const payload = {
                note_name: formValues.day
            };

            const response = selectedRow
                ? await UpdatePracticeNotes(selectedRow.daily_note_id, selection.coe, selection.lab, formValues.makerPlan, formValues.module, formValues.topic, payload)
                : await addPracticeNotes(selection.coe, selection.lab, formValues.makerPlan, formValues.module, formValues.topic, payload);
            toast.success(response.data.message);
            resetFormState();
            onClose();
            fetchData();
        } catch (error) {
            toast.error(error?.response?.data?.message || error.message || 'An unexpected error occurred.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '45%',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}
            >
                <Typography variant="h6" sx={{ mb: 2 }}>{selectedRow ? "Update Practice Notes " : "Create Practice Notes"}</Typography>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        {[
                            { label: 'Maker Plan *', name: 'makerPlan', type: 'select', options: makerPlanOptions, error: formErrors.makerPlan },
                            { label: 'Module *', name: 'module', type: 'select', options: makerModuleOptions, error: formErrors.module },
                            { label: 'Topic Name *', name: 'topic', type: 'select', options: topicOptions, error: formErrors.topic },
                            { label: 'Day *', name: 'day', type: 'text', error: formErrors.day },
                        ].map(({ label, name, type, options, error, ...props }) => (
                            <Grid item xs={12} key={name}>
                                {type === 'select' ? (
                                    <TextField
                                        fullWidth
                                        select
                                        label={label}
                                        name={name}
                                        value={formValues[name]}
                                        onChange={handleInputChange}
                                        error={!!error}
                                        helperText={error}
                                        size='small'
                                    >
                                        {name === "makerPlan" && options.map((option, index) => (
                                            <MenuItem key={index} value={option.makerplanID}>{option.makerplan_Name}</MenuItem>
                                        ))}
                                        {name === "module" && options.Maker_Modules?.map((option, index) => (
                                            <MenuItem key={index} value={option.moduleID}>{option.module_Name}</MenuItem>
                                        ))}
                                        {name === "topic" && options?.map((option, index) => (
                                            <MenuItem key={index} value={option.topicID}>{option.topic_Name}</MenuItem>
                                        ))}
                                    </TextField>
                                ) : (
                                    <TextField
                                        fullWidth
                                        size='small'
                                        type={type}
                                        label={label}
                                        name={name}
                                        value={formValues[name]}
                                        onChange={handleInputChange}
                                        error={!!error}
                                        helperText={error}
                                        {...props}
                                    />
                                )}
                            </Grid>
                        ))}
                        <Grid item xs={12} sx={{ textAlign: 'center' }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={isLoading}
                                sx={{ marginRight: 2 }}
                            >
                                {isLoading ? <CircularProgress size={24} /> : 'Submit'}
                            </Button>
                            <Button variant="outlined" color="secondary" onClick={onClose}>
                                Cancel
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Box>
        </Modal>
    );
};

export default NotesPracticeModal;
